import * as React from "react";
import styled from "styled-components";
import LocationIcon from "../../../assets/images/location_white_icon.svg";
import { Link } from "../../shared/Link";
import { get } from "lodash";
import { toREM, toVWDesktop } from "../../../helpers/styles";
import * as CommonHeaderTypes from "../types";
import { useEffect, useState } from "react";

export type DrawerProps = {
  className?: string;
  subMenuItem: CommonHeaderTypes.SubMenuItemWithOptions;
  productsVariant?: boolean;
};

const DrawerSubMenuItemView = ({
  className,
  subMenuItem,
  productsVariant = false,
}: DrawerProps) => {
  const [selectedItem, setSelectedItem] =
    useState<CommonHeaderTypes.Item | undefined>();

  useEffect(() => {
    setSelectedItem(get(subMenuItem, "options[0].items[0]", undefined));
  }, [subMenuItem]);

  const visibleOptions = subMenuItem?.options || []

  return (
    <Container className={className} $products={productsVariant}>
      <Content $products={productsVariant}>
        {visibleOptions?.map((option, i, options) => (
          <>
            <GroupTitle $products={productsVariant}>{option.name}</GroupTitle>
            {option?.items?.map((item, index) => (
              <ItemNameWrapper key={index} $products={productsVariant}>
                <ItemName
                  to={item?.menuItemLink || "#"}
                  $products={productsVariant}
                  active={item?.name === selectedItem?.name}
                  onClick={() => setSelectedItem(item)}
                  onMouseEnter={() => setSelectedItem(item)}
                  onFocus={() => setSelectedItem(item)}
                >
                  {item.name}
                </ItemName>
              </ItemNameWrapper>
            ))}
            {i < options.length - 1 && <Spacer />}
          </>
        ))}
      </Content>
      <Content $products={productsVariant}>
        {selectedItem && (
          <ItemContainer $products={productsVariant}>
            <ItemImage
              $products={productsVariant}
              src={selectedItem?.image}
              alt={selectedItem?.imageAlt}
            />
            <ItemActions $products={productsVariant}>
              <PrimaryButton
                $products={productsVariant}
                to={selectedItem?.primaryButtonLink || ""}
              >
                {selectedItem?.primaryButtonLabel}
              </PrimaryButton>
              <SecondaryButton
                $products={productsVariant}
                to={selectedItem?.secondaryButtonLink || ""}
              >
                {!productsVariant && (
                  <Icon
                    src={LocationIcon}
                    alt={selectedItem?.secondaryButtonLabel}
                  />
                )}
                {selectedItem?.secondaryButtonLabel}
              </SecondaryButton>
            </ItemActions>
          </ItemContainer>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div<{ $products?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const Content = styled.div<{ $products?: boolean }>`
  flex: ${(props) => (props.$products ? "0 0 auto" : "1 0 50%")};
`;

const GroupTitle = styled.div<{
  noPaddingTop?: boolean;
  active?: boolean;
  $products?: boolean;
}>`
  color: ${(props) => (props.$products ? "#012604" : "#ffffff")};
  font-family: ${(props) =>
    props.$products
      ? "GT Alpina"
      : `${props.theme.fontFamily}, ${props.theme.fallBackFontFamily}, sans-serif`};
  font-size: ${(props) =>
    props.$products ? `${toREM(32)}rem` : `${toREM(24)}rem`};
  line-height: ${(props) => (props.$products ? "1" : `${toREM(31)}rem`)};
  font-weight: ${(props) => (props.$products ? 300 : 900)};
  letter-spacing: -0.05em;
  text-transform: ${(props) => (props.$products ? "none" : "uppercase")};
  width: fit-content;
  margin-bottom: ${(props) =>
    props.$products ? `${toVWDesktop(8)}vw` : `${toVWDesktop(7)}vw`};
  max-width: ${(props) => (props.$products ? `${toVWDesktop(388)}vw` : "none")};
`;

const ItemNameWrapper = styled.div<{ $products?: boolean }>`
  display: flex;
  flex-direction: column;

  &:not(:first-of-type) {
    margin-top: ${(props) => (props.$products ? `${toVWDesktop(8)}vw` : "0")};
  }
`;

const ItemName = styled(Link)<{
  noPaddingTop?: boolean;
  active?: boolean;
  $products?: boolean;
}>`
  cursor: pointer;
  color: ${(props) =>
    props.$products
      ? props.active
        ? "#b5dd71"
        : "#012604"
      : props.active
      ? "#75C154"
      : "#ffffff"};
  background-color: ${(props) =>
    props.$products && props.active ? "#012604" : "transparent"};
  border-radius: ${(props) =>
    props.$products && props.active ? `${toVWDesktop(3)}vw` : "0"};
  font-family: ${(props) =>
    props.$products
      ? "GT Alpina"
      : `${props.theme.fontFamily}, ${props.theme.fallBackFontFamily}, sans-serif`};
  font-size: ${toREM(16)}rem;
  line-height: ${(props) => (props.$products ? "1" : `${toREM(24)}rem`)};
  font-weight: ${(props) => (props.$products ? 300 : 800)};
  letter-spacing: ${(props) =>
    props.$products ? `${toREM(-0.64)}rem` : "0.06em"};
  width: fit-content;
  text-decoration: none;
  top: ${(props) => (props.$products ? "0" : `${toVWDesktop(7)}vw`)};
  margin-top: ${(props) =>
    props.$products ? "0" : `${toVWDesktop(7)}vw`};
  margin-left: ${(props) => (props.$products ? "0" : `${toVWDesktop(7)}vw`)};
  margin-bottom: ${(props) => (props.$products ? "0" : `${toVWDesktop(7)}vw`)};
  padding: ${(props) => (props.$products ? `${toVWDesktop(7)}vw` : "0")};
  white-space: nowrap;
  border-radius: ${(props) => (props.$products ? `${toVWDesktop(3)}vw` : "0")};
  transition: background-color 220ms ease, color 220ms ease;
`;

const Spacer = styled.div`
  height: ${toVWDesktop(40)}vw;
`;

const ItemContainer = styled.div<{ $products?: boolean }>`
  width: ${(props) =>
    props.$products ? `${toVWDesktop(395)}vw` : `${toVWDesktop(290)}vw`};
  max-width: 100%;
  display: flex;
  flex-direction: column;
`;

const ItemImage = styled.img<{ $products?: boolean }>`
  object-fit: contain;
  width: ${(props) => (props.$products ? "100%" : "auto")};
  height: ${toVWDesktop(219)}vw;
  max-width: 100%;
  max-height: ${(props) => (props.$products ? `${toVWDesktop(219)}vw` : "none")};
  display: block;
  margin-left: auto;
  margin-right: auto;
  margin-top: ${(props) => (props.$products ? `${toVWDesktop(10)}vw` : "0")};
`;

const ItemActions = styled.div<{ $products?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$products ? `${toVWDesktop(12)}vw` : "0")};
  margin-top: ${(props) =>
    props.$products ? `${toVWDesktop(31)}vw` : `${toVWDesktop(10)}vw`};
`;

const PrimaryButton = styled(Link)<{ $products?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: ${(props) => (props.$products ? "0 0 auto" : `1 0 ${toREM(150)}rem`)};
  font-family: ${(props) =>
    props.$products
      ? "GT Alpina"
      : `${props.theme.fontFamily}, ${props.theme.fallBackFontFamily}, sans-serif`};
  font-size: ${toREM(16)}rem;
  line-height: 1;
  font-weight: 300;
  letter-spacing: ${toREM(-0.64)}rem;
  border: ${(props) => (props.$products ? "none" : "3px solid #ffffff")};
  border-radius: ${(props) =>
    props.$products ? `${toVWDesktop(431)}vw` : "0"};
  background-color: ${(props) => (props.$products ? "#b5dd71" : "transparent")};
  padding: ${(props) =>
    props.$products
      ? `${toVWDesktop(16)}vw ${toVWDesktop(32)}vw`
      : `${toVWDesktop(10)}vw ${toVWDesktop(29)}vw`};
  cursor: pointer;
  text-decoration: none;
  color: ${(props) => (props.$products ? "#012604" : "#ffffff")};
  height: fit-content;
`;

const SecondaryButton = styled(Link)<{ $products?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  direction: ${(props) => (props.theme.isRTL ? "rtl" : "ltr")};
  font-family: ${(props) =>
    props.$products
      ? "GT Alpina"
      : `${props.theme.fontFamily}, ${props.theme.fallBackFontFamily}, sans-serif`};
  font-size: ${(props) =>
    props.$products ? `${toREM(16)}rem` : `${toREM(14)}rem`};
  line-height: ${(props) => (props.$products ? "1" : `${toREM(18)}rem`)};
  font-weight: ${(props) => (props.$products ? 300 : 800)};
  letter-spacing: ${(props) => (props.$products ? `${toREM(-0.64)}rem` : "0")};
  border: ${(props) =>
    props.$products ? `1px solid #012604` : "none"};
  border-radius: ${(props) =>
    props.$products ? `${toVWDesktop(431)}vw` : "0"};
  padding: ${(props) =>
    props.$products
      ? `${toVWDesktop(16)}vw ${toVWDesktop(32)}vw`
      : props.theme.isRTL
      ? `${toVWDesktop(10)}vw ${toVWDesktop(17)}vw ${toVWDesktop(
          10,
        )}vw ${toVWDesktop(0)}vw`
      : `${toVWDesktop(10)}vw ${toVWDesktop(0)}vw ${toVWDesktop(
          10,
        )}vw ${toVWDesktop(17)}vw`};
  cursor: pointer;
  text-decoration: none;
  color: ${(props) => (props.$products ? "#012604" : "#ffffff")};
  height: fit-content;
`;

const Icon = styled.img`
  object-fit: contain;
  width: ${toVWDesktop(18.62)}vw;
  margin: ${(props) =>
    props.theme.isRTL
      ? `0 0 0 ${toVWDesktop(8.6)}vw`
      : `0 ${toVWDesktop(8.6)}vw 0 0`};
  height: auto;
`;

export default DrawerSubMenuItemView;
